/**
 * CameraController.jsx - Simplified camera movement controller
 * Handles smooth camera animations to target positions and look-at points
 *
 * Uses the camera-controls library API (@react-three/drei CameraControls):
 *   controls.setLookAt(px, py, pz, tx, ty, tz, enableTransition)
 *   controls.getPosition(out)
 *   controls.getTarget(out)
 * NOT OrbitControls — there is no .target property on camera-controls instances.
 */

import React, { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { easeInOutCubic } from './CameraService.js'
import { ANIMATION_TIMINGS } from './CameraConfig.js'

// ============================================================================
// CAMERA MOVEMENT CONTROLLER
// ============================================================================

/**
 * CameraMovementController - Smooth animation of camera position and look-at target
 * @param {Object} props
 * @param {THREE.Camera} props.camera - The camera to animate (REQUIRED - must be passed, not from useThree)
 * @param {Object} props.controls - The camera-controls instance (REQUIRED - must be passed)
 * @param {THREE.Vector3} props.targetPosition - Target camera position
 * @param {THREE.Vector3} props.targetLookAt - Target look-at position
 * @param {boolean} props.animate - Whether to start animation
 * @param {number} props.duration - Animation duration in milliseconds
 * @param {Function} props.onAnimationComplete - Callback when animation finishes
 */
export function CameraMovementController({
  camera,
  controls,
  targetPosition,
  targetLookAt,
  animate = false,
  duration = ANIMATION_TIMINGS.cameraMovement,
  onAnimationComplete
}) {
  const animRef = useRef({
    isAnimating: false,
    startPosition: null,
    startTarget: null,
    startTime: null,
    targetPosition: null,
    targetLookAt: null,
  })

  // Initialize animation on mount / prop change
  useEffect(() => {
    if (animate && targetPosition && targetLookAt && camera && controls) {
      // camera-controls API: getPosition / getTarget read the current values
      const startPos = new THREE.Vector3()
      const startTar = new THREE.Vector3()
      controls.getPosition(startPos)
      controls.getTarget(startTar)

      animRef.current.startPosition = startPos
      animRef.current.startTarget = startTar
      animRef.current.startTime = performance.now()
      animRef.current.isAnimating = true
      animRef.current.targetPosition = targetPosition.clone ? targetPosition.clone() : new THREE.Vector3().copy(targetPosition)
      animRef.current.targetLookAt = targetLookAt.clone ? targetLookAt.clone() : new THREE.Vector3().copy(targetLookAt)

      // Disable controls during animation
      controls.enabled = false
    }
  }, [animate, targetPosition, targetLookAt, camera, controls])

  // Clean up when unmounted or animate flips to false
  useEffect(() => {
    if (!animate) {
      animRef.current.isAnimating = false
      animRef.current.startTime = null
      animRef.current.startPosition = null
      animRef.current.startTarget = null

      // Re-enable controls
      if (controls) {
        controls.enabled = true
      }
    }
  }, [animate, controls])

  useFrame(() => {
    const anim = animRef.current
    if (!anim.isAnimating || !anim.startTime || !anim.startPosition || !anim.startTarget) return

    const elapsed = performance.now() - anim.startTime
    const progress = Math.min(elapsed / duration, 1)
    const easedProgress = easeInOutCubic(progress)

    const finalPos = anim.targetPosition
    const finalTar = anim.targetLookAt

    // Interpolate camera position and look-at using lerp
    const currentPos = new THREE.Vector3().lerpVectors(anim.startPosition, finalPos, easedProgress)
    const currentTar = new THREE.Vector3().lerpVectors(anim.startTarget, finalTar, easedProgress)

    // camera-controls: setLookAt(px,py,pz, tx,ty,tz, enableTransition=false)
    // enableTransition=false = instant snap to our manually-lerped value each frame
    controls.setLookAt(
      currentPos.x, currentPos.y, currentPos.z,
      currentTar.x, currentTar.y, currentTar.z,
      false
    )

    // Animation complete
    if (progress >= 1) {
      // Snap to exact final values
      controls.setLookAt(
        finalPos.x, finalPos.y, finalPos.z,
        finalTar.x, finalTar.y, finalTar.z,
        false
      )

      controls.enabled = true

      anim.isAnimating = false
      anim.startTime = null
      anim.startPosition = null
      anim.startTarget = null

      if (onAnimationComplete) {
        onAnimationComplete()
      }
    }
  })

  return null
}
